'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { SetupConnectTutorialStep } from '@/components/setup/experience/lib/connectTutorialTypes';
import { cn } from '@/lib/utils';

const STEP_MOTION = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
};

interface SetupConnectTutorialContentProps {
  className?: string;
  steps: SetupConnectTutorialStep[];
  stepOfLabel: (current: number, total: number) => string;
  segmentsAriaLabel: string;
  goToStepAriaLabel: (step: number) => string;
  /** 'split' = image beside text (dialogs); 'stacked' = image above text (narrow panels). */
  layout?: 'split' | 'stacked';
  /** When set, the step image becomes a button (e.g. to open a zoomed view). */
  onImageClick?: (step: SetupConnectTutorialStep) => void;
  imageZoomAriaLabel?: string;
}

export function SetupConnectTutorialContent({
  className,
  steps,
  stepOfLabel,
  segmentsAriaLabel,
  goToStepAriaLabel,
  layout = 'split',
  onImageClick,
  imageZoomAriaLabel,
}: SetupConnectTutorialContentProps) {
  const { t } = useTranslation('setup');
  const [currentStep, setCurrentStep] = React.useState(1);

  const stepCount = steps.length;
  const step = steps[currentStep - 1];
  const isFirst = currentStep === 1;
  const isLast = currentStep === stepCount;
  const progressRatio = currentStep / stepCount;
  const hasMedia = Boolean(step?.image);

  React.useEffect(() => {
    setCurrentStep(1);
  }, [steps]);

  const goToStep = (next: number) => {
    setCurrentStep(Math.max(1, Math.min(next, stepCount)));
  };

  if (!step) return null;

  return (
    <div className={cn(className)}>
      {layout === 'split' ? (
        <div className="px-5 pb-3.5 sm:px-6" aria-hidden>
          <div className="mb-[0.4375rem] flex justify-end">
            <span className="text-muted-foreground inline-flex rounded-full bg-[color-mix(in_oklch,var(--muted)_38%,transparent)] px-2 py-[0.1875rem] text-[0.6875rem] font-medium tracking-[0.01em]">
              {stepOfLabel(currentStep, stepCount)}
            </span>
          </div>
          <div className="h-0.5 overflow-hidden rounded-full bg-[color-mix(in_oklch,var(--muted-foreground)_14%,transparent)]">
            <motion.div
              className="block h-full w-full origin-left rounded-[inherit] bg-[linear-gradient(90deg,color-mix(in_oklch,var(--primary)_72%,transparent),var(--primary))]"
              initial={false}
              animate={{ scaleX: progressRatio }}
              transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-4 px-5 pb-1 sm:px-6">
        <AnimatePresence mode="wait" initial={false}>
          <motion.article
            key={step.id}
            className={cn(
              'grid grid-cols-[minmax(0,1fr)] gap-4',
              layout === 'split' &&
                'sm:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] sm:items-start sm:gap-[1.125rem]',
            )}
            initial={STEP_MOTION.initial}
            animate={STEP_MOTION.animate}
            exit={STEP_MOTION.exit}
            transition={STEP_MOTION.transition}
          >
            {hasMedia ? (
              onImageClick ? (
                <button
                  type="button"
                  className={cn(
                    'group relative cursor-zoom-in overflow-hidden rounded-[0.625rem] border border-[color-mix(in_oklch,var(--border)_82%,transparent)] bg-[color-mix(in_oklch,var(--background)_88%,var(--card))] p-0 text-left shadow-[inset_0_1px_0_color-mix(in_oklch,var(--card)_60%,transparent)] transition-[border-color] duration-150 hover:border-[color-mix(in_oklch,var(--primary)_38%,var(--border))]',
                    layout === 'stacked' && 'h-[13rem]',
                  )}
                  aria-label={imageZoomAriaLabel ?? step.title}
                  onClick={() => onImageClick(step)}
                >
                  <img
                    src={step.image}
                    alt={step.imageAlt ?? step.title}
                    className={cn(
                      'block w-full object-contain',
                      layout === 'stacked' ? 'h-full' : 'max-h-[min(36vh,15rem)] object-left-top',
                    )}
                    loading="lazy"
                    draggable={false}
                  />
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[color-mix(in_oklch,var(--foreground)_0%,transparent)] opacity-0 transition-opacity duration-150 group-hover:bg-[color-mix(in_oklch,var(--foreground)_8%,transparent)] group-hover:opacity-100">
                    <span className="bg-card/95 rounded-full p-1.5 shadow-[0_1px_4px_color-mix(in_oklch,var(--foreground)_18%,transparent)]">
                      <Maximize2 className="text-foreground size-3.5" aria-hidden />
                    </span>
                  </span>
                </button>
              ) : (
                <div
                  className={cn(
                    'overflow-hidden rounded-[0.625rem] border border-[color-mix(in_oklch,var(--border)_82%,transparent)] bg-[color-mix(in_oklch,var(--background)_88%,var(--card))] shadow-[inset_0_1px_0_color-mix(in_oklch,var(--card)_60%,transparent)]',
                    layout === 'stacked' && 'h-[13rem]',
                  )}
                >
                  <img
                    src={step.image}
                    alt={step.imageAlt ?? step.title}
                    className={cn(
                      'block w-full object-contain',
                      layout === 'stacked' ? 'h-full' : 'max-h-[min(36vh,15rem)] object-left-top',
                    )}
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              )
            ) : null}

            <div
              className={cn(
                'flex min-w-0 flex-col gap-1.5 pt-0.5',
                !hasMedia && 'pt-0',
                layout === 'stacked' && 'min-h-[7rem]',
              )}
            >
              <p className="m-0 text-[0.6875rem] font-semibold tracking-[0.14em] text-[color-mix(in_oklch,var(--primary)_70%,var(--muted-foreground))]">
                {String(currentStep).padStart(2, '0')}
                <span className="mx-[0.1875rem] opacity-[0.45]" aria-hidden>
                  /
                </span>
                {String(stepCount).padStart(2, '0')}
              </p>
              <h3 className="text-foreground m-0 text-[0.9375rem] font-semibold leading-[1.35] tracking-[-0.015em]">
                {step.title}
              </h3>
              <p className="text-muted-foreground m-0 text-[0.8125rem] leading-[1.6]">{step.instructions}</p>
              {step.content ? <div className="mt-1.5">{step.content}</div> : null}
            </div>
          </motion.article>
        </AnimatePresence>

        <div
          className="grid grid-cols-[repeat(var(--sx-tutorial-segments,9),minmax(0,1fr))] gap-[0.3125rem]"
          role="tablist"
          aria-label={segmentsAriaLabel}
          style={{ ['--sx-tutorial-segments' as string]: steps.length }}
        >
          {steps.map((item, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === currentStep;
            const isDone = stepNumber < currentStep;

            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={goToStepAriaLabel(stepNumber)}
                className={cn(
                  'duration-[220ms] h-[0.1875rem] cursor-pointer rounded-full border-0 p-0 transition-[background,transform]',
                  !isDone &&
                    !isActive &&
                    'bg-[color-mix(in_oklch,var(--muted-foreground)_18%,transparent)] hover:bg-[color-mix(in_oklch,var(--primary)_38%,var(--muted-foreground))]',
                  isDone && 'bg-[color-mix(in_oklch,var(--primary)_52%,var(--muted-foreground))]',
                  isActive && 'bg-primary scale-y-[1.6]',
                )}
                onClick={() => goToStep(stepNumber)}
              />
            );
          })}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-[color-mix(in_oklch,var(--border)_72%,transparent)] pt-3.5 max-[639px]:flex-col max-[639px]:items-stretch">
          <button
            type="button"
            className="text-muted-foreground hover:enabled:text-foreground inline-flex min-h-[2.125rem] cursor-pointer items-center justify-center gap-[0.3125rem] rounded-[0.4375rem] border-0 bg-transparent px-3 text-[0.8125rem] font-medium leading-none transition-[background,border-color,color,opacity] duration-150 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-[0.32] max-[639px]:w-full max-[639px]:justify-start max-[639px]:px-0"
            disabled={isFirst}
            onClick={() => goToStep(currentStep - 1)}
          >
            <ChevronLeft className="size-4" strokeWidth={2} aria-hidden />
            {t('experience.connect.shopifyTutorialPrevious')}
          </button>
          <button
            type="button"
            className="text-primary ml-auto inline-flex min-h-[2.125rem] min-w-[7.5rem] cursor-pointer items-center justify-center gap-[0.3125rem] rounded-[0.4375rem] border border-[var(--crm-selection-hover-border)] bg-[var(--crm-selection-bg)] px-3 text-[0.8125rem] font-medium leading-none transition-[background,border-color,color,opacity] duration-150 hover:enabled:border-[var(--crm-selection-border)] hover:enabled:bg-[color-mix(in_oklch,var(--primary)_12%,var(--card))] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-[0.32] max-[639px]:ml-0 max-[639px]:w-full"
            disabled={isLast}
            onClick={() => goToStep(currentStep + 1)}
          >
            {t('experience.connect.shopifyTutorialNext')}
            <ChevronRight className="size-4" strokeWidth={2.25} aria-hidden />
          </button>
        </footer>
      </div>
    </div>
  );
}
